import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/image-compress";
import { createSupportTicket, SUPPORT_CATEGORIES } from "@/lib/support.functions";

type Category = (typeof SUPPORT_CATEGORIES)[number]["value"];

export function SupportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const submit = useServerFn(createSupportTicket);

  const [category, setCategory] = useState<Category>("boost");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [locked, setLocked] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void supabase.auth.getUser().then(({ data }) => {
      const user = data.user;
      if (!user) return;
      setEmail(user.email ?? "");
      const fullName = (user.user_metadata?.["full_name"] as string | undefined) ?? "";
      if (fullName) setName(fullName);
      setLocked(!!user.email);
    });
  }, [open]);

  function reset() {
    setSubject("");
    setMessage("");
    setFile(null);
    setTicketNumber(null);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (subject.trim().length < 3) {
      toast.error("Bitte gib einen kurzen Betreff ein.");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("Bitte beschreibe dein Anliegen etwas genauer.");
      return;
    }
    setBusy(true);
    try {
      let attachment: string | undefined;
      if (file) {
        const compressed = await compressImage(file);
        attachment = await blobToDataUrl(compressed.blob);
      }

      const result = await submit({
        data: {
          category,
          subject: subject.trim(),
          message: message.trim(),
          email: email.trim(),
          name: name.trim() || undefined,
          attachment,
          attachmentName: file?.name,
          context: {
            page: typeof window !== "undefined" ? window.location.pathname : "",
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
          },
        },
      });

      if (!result.ok) {
        toast.error(
          result.error === "rate_limited"
            ? "Du hast gerade mehrere Anfragen gesendet. Bitte warte kurz."
            : "Das hat nicht funktioniert. Bitte versuche es erneut.",
        );
        return;
      }
      setTicketNumber(result.ticketNumber);
    } catch {
      toast.error("Das hat nicht funktioniert. Bitte versuche es erneut.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {ticketNumber ? (
          <div className="py-6 text-center">
            <div className="mx-auto grid size-12 place-items-center rounded-full bg-primary/10">
              <CheckCircle2 className="size-6 text-primary" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-normal">Anfrage ist da</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Deine Ticket-Nummer: <span className="font-medium text-foreground">{ticketNumber}</span>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Wir antworten in der Regel am nächsten Werktag an {email}.
            </p>
            <Button className="mt-6" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-normal">
                Support kontaktieren
              </DialogTitle>
              <DialogDescription>
                Beschreibe kurz, was nicht passt – wir melden uns in der Regel am nächsten Werktag.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="support-category">Worum geht es?</Label>
                <Select value={category} onValueChange={(value) => setCategory(value as Category)}>
                  <SelectTrigger id="support-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_CATEGORIES.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="support-subject">Betreff</Label>
                <Input
                  id="support-subject"
                  value={subject}
                  maxLength={120}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="z. B. Boost für Donnerstag wird nicht angezeigt"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="support-message">Beschreibung</Label>
                <Textarea
                  id="support-message"
                  value={message}
                  maxLength={4000}
                  rows={6}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={"Was hast du gemacht?\nWas ist passiert?\nWelcher Tag bzw. welches Datum betrifft es?"}
                  required
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="support-name">Name</Label>
                  <Input
                    id="support-name"
                    value={name}
                    maxLength={120}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Vor- und Nachname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-email">E-Mail</Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={email}
                    readOnly={locked}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="dein@salon.de"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="support-file">Screenshot (optional)</Label>
                {file ? (
                  <div className="flex items-center justify-between gap-3 rounded-2xl border bg-secondary/40 px-4 py-3 text-sm">
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      aria-label="Anhang entfernen"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="support-file"
                    className="flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed px-4 py-3 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <Paperclip className="size-4" />
                    Bild auswählen
                  </label>
                )}
                <input
                  id="support-file"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Wir schicken automatisch mit, auf welcher Seite du warst und welchen Salon dein
                Konto hat – damit wir nicht nachfragen müssen.
              </p>

              <Button type="submit" disabled={busy} className="w-full">
                {busy ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" /> Wird gesendet
                  </>
                ) : (
                  "Anfrage senden"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}
